import ActiveToggleButton from "@/components/ActiveToggleButton";
import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import SLAConfigurationService from "@/services/APIs/SLAConfigurationService";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { SLAConfiguration } from "@/utils/types/slaConfiguration";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm, Tag } from "antd";
import dayjs from "dayjs";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const SLAListPage = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Fetch SLA configurations data
  const {
    data: slaConfigs,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["sla-configurations", page, pageSize],
    queryFn: () =>
      SLAConfigurationService.getAllConfigurations({ page, pageSize }),
  });

  const gridData = slaConfigs?.results ?? [];
  const total = slaConfigs?.count ?? 0;

  // Handle delete SLA configuration
  const handleDelete = async (id: string) => {
    setIsDeleting(id);
    try {
      await SLAConfigurationService.deleteConfiguration(id);
      refetch();
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setIsDeleting(null);
    }
  };

  // Handle edit SLA configuration
  const handleEdit = (id: string) => {
    navigate(navigationRoutes.SLA_EDIT(id));
  };

  // Handle add new SLA configuration
  const handleAdd = () => {
    navigate(navigationRoutes.SLA_ADD);
  };

  // Check if any action is in progress
  const isAnyActionInProgress = isLoading || isDeleting !== null;

  // Table columns configuration
  const columns: ProColumns<SLAConfiguration>[] = [
    {
      title: "SLA Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name!.localeCompare(b.name!),
    },

    {
      title: "Default SLA",
      dataIndex: "default_service_level_agreement",
      key: "default_service_level_agreement",
      width: 120,
      align: "center",
      render: value => (value ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      width: 120,
      render: (_, record) =>
        record.start_date ? dayjs(record.start_date).format("DD-MM-YYYY") : "—",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      width: 120,
      render: (_, record) =>
        record.end_date ? dayjs(record.end_date).format("DD-MM-YYYY") : "—",
    },
    {
      title: "Enabled",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.sla_configuration}
            pk={record.id}
            is_active={record.enabled ?? false}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record.id)}
          type="text"
          title="Edit SLA Configuration"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this SLA configuration?"
          description="This action cannot be undone."
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
          disabled={isAnyActionInProgress}
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete SLA Configuration"
            loading={isDeleting === record.id}
            disabled={isAnyActionInProgress}
          />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <BaseTable<SLAConfiguration>
      loading={isLoading}
      columns={columns}
      dataSource={gridData}
      page={page}
      pageSize={pageSize}
      total={total}
      rowKey="id"
      toolBarRender={() => [
        <CustomButton
          key="add"
          type="primary"
          icon={<Plus size={16} />}
          onClick={handleAdd}
          disabled={isAnyActionInProgress}
          title="Add New SLA Configuration"
        >
          Add SLA
        </CustomButton>,
      ]}
      headerTitle="SLA Configurations"
      onPageChange={(page, size) => {
        setPage(page);
        setPageSize(size ?? 10);
      }}
      request={() => refetch()}
      tableName={gridName.slaConfigurationTable}
    />
  );
};

export default SLAListPage;
